/**
 * adminFetch — wraps fetch() for /api/admin/* calls.
 *
 * - Automatically injects Authorization: Bearer <token>.
 * - On 401, clears the stored token and invokes onUnauthorized callback
 *   so the UI can show a "session expired" screen.
 * - On non-ok responses, throws an Error with the server's error message.
 */

const STORAGE_KEY = 'admin_token';

let _onUnauthorized = null;

/** Register a callback that fires when the server returns 401. */
export function setUnauthorizedHandler(cb) {
  _onUnauthorized = cb;
}

export function getStoredToken() {
  return sessionStorage.getItem(STORAGE_KEY);
}

/**
 * Make an authenticated request to /api/admin/*.
 *
 * @param {string} path — relative path starting with /api/admin/...
 * @param {RequestInit} opts — standard fetch options
 * @returns {Promise<any>} — parsed JSON response body
 */
export async function adminFetch(path, opts = {}) {
  const token = getStoredToken();

  const headers = {
    ...(opts.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  // Only set Content-Type for JSON bodies — uploads pass their own type
  if (opts.body && typeof opts.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(path, { ...opts, headers });

  if (res.status === 401) {
    sessionStorage.removeItem(STORAGE_KEY);
    if (_onUnauthorized) _onUnauthorized();
    throw new Error('Session expired — please return to ICON to get a fresh link');
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body.error || (body.errors ? body.errors.join(', ') : msg);
    } catch { /* empty */ }
    throw new Error(msg);
  }

  // 204 No Content — return null
  if (res.status === 204) return null;

  return res.json();
}

/** Convenience helpers */

export const adminGet = (path) => adminFetch(path, { method: 'GET' });

export const adminPost = (path, data) =>
  adminFetch(path, { method: 'POST', body: JSON.stringify(data) });

export const adminPatch = (path, data) =>
  adminFetch(path, { method: 'PATCH', body: JSON.stringify(data) });

export const adminDelete = (path) =>
  adminFetch(path, { method: 'DELETE' });

/**
 * Upload a file to /api/admin/uploads.
 * Sends the raw file bytes with Content-Type from the File object
 * and X-Filename header.
 */
export async function adminUpload(file) {
  return adminFetch('/api/admin/uploads', {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Filename': file.name,
    },
    body: file,
  });
}
